import { c as _c } from "react/compiler-runtime";
import { cn } from "cn";
import type * as React from "react";
import { FormLabel, type FormLabelProps } from "./FormLabel";

/** Stacks related fields, such as the fields of one form section. */
function FieldGroup(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex w-full flex-col gap-8", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div data-slot="field-group" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}

/** One field: its heading, its control, and any description or error under it. */
function Field(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex w-full flex-col gap-2", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div data-slot="field" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
interface FieldHeadingProps {
  id?: string;
  htmlFor?: string;
  title: React.ReactNode;
  required?: boolean;
  description?: React.ReactNode;
  /** The description's id, for a control to point at with `aria-describedby`. */
  descriptionId?: string;
  size?: NonNullable<FormLabelProps["size"]>;
}
const descriptionTypography = {
  large: "body-md",
  medium: "body-sm",
  small: "body-sm"
} as const;

/**
 * A field's title with an optional description under it. Give it `htmlFor`
 * when it labels a single control, or `id` when a group of controls points at
 * it with `aria-labelledby`.
 */
function FieldHeading(t0) {
  const $ = _c(15);
  const {
    id,
    htmlFor,
    title,
    required,
    description,
    descriptionId,
    size: t1
  } = t0;
  const size = t1 === undefined ? "medium" : t1;
  let t2;
  if ($[0] !== required) {
    t2 = required && <span aria-hidden="true">*</span>;
    $[0] = required;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== htmlFor || $[3] !== id || $[4] !== size || $[5] !== t2 || $[6] !== title) {
    t3 = <FormLabel id={id} htmlFor={htmlFor} size={size}>{title}{t2}</FormLabel>;
    $[2] = htmlFor;
    $[3] = id;
    $[4] = size;
    $[5] = t2;
    $[6] = title;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== description || $[9] !== descriptionId || $[10] !== size) {
    t4 = description && <p id={descriptionId} className={cn(descriptionTypography[size], "text-muted-foreground")}>{description}</p>;
    $[8] = description;
    $[9] = descriptionId;
    $[10] = size;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t3 || $[13] !== t4) {
    t5 = <div data-slot="field-heading">{t3}{t4}</div>;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}

/** Helper text under a control. */
function FieldDescription(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("body-sm text-muted-foreground", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <p data-slot="field-description" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}

/** Validation message under a control, announced when it appears. */
function FieldError(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("body-sm text-(--notification-destructive-fg)", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <p role="alert" data-slot="field-error" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export { Field, FieldDescription, FieldError, FieldGroup, FieldHeading, type FieldHeadingProps };